import { PositionInstructions, StartEndRowCol } from "@coolset/cs-ui";
import { Typography } from "@mui/material";
import React, { useMemo } from "react";

const MAX_GRID_SIZE = 40;
const CELL_PX = 16;

type CellState = "empty" | "occupied" | "preview" | "conflict" | "expand" | "preview-expand";

const CELL_BACKGROUNDS: Record<CellState, string> = {
  empty: "#ffffff",
  occupied: "#bdbdbd",
  preview: "#90caf9",
  conflict: "#ef9a9a",
  expand: "#f5f5f5",
  "preview-expand": "linear-gradient(to bottom right, #90caf9 50%, #f5f5f5 50%)",
};

const LEGEND: { state: CellState; label: string }[] = [
  { state: "occupied", label: "Occupied" },
  { state: "preview", label: "Paste preview" },
  { state: "conflict", label: "Conflict" },
  { state: "expand", label: "Will expand table" },
  { state: "preview-expand", label: "Paste preview (expands table)" },
];

export interface TablePastePreviewProps {
  tableScale: { maxRows: number; maxColumns: number } | null;
  positionInstructionsToPaste: PositionInstructions | null;
  copiedPositionInstructions: PositionInstructions | null;
  pasteAnchorRow: number;
  pasteAnchorCol: number;
  setPasteAnchorRow: (row: number) => void;
  setPasteAnchorCol: (col: number) => void;
}

export const TablePastePreview = ({
  tableScale,
  positionInstructionsToPaste,
  copiedPositionInstructions,
  pasteAnchorRow,
  pasteAnchorCol,
  setPasteAnchorRow,
  setPasteAnchorCol,
}: TablePastePreviewProps) => {
  const { cellStates, gridMaxRow, gridMaxCol } = useMemo(() => {
    const copiedPositions = Object.values(copiedPositionInstructions ?? {}).filter(
      (p): p is StartEndRowCol => p !== undefined
    );

    const minRow = copiedPositions.length ? Math.min(...copiedPositions.map((p) => p.rowStart)) : 1;
    const minCol = copiedPositions.length ? Math.min(...copiedPositions.map((p) => p.colStart)) : 1;
    const rowOffset = pasteAnchorRow - minRow;
    const colOffset = pasteAnchorCol - minCol;

    const occupied = new Set(
      Object.values(positionInstructionsToPaste ?? {})
        .filter((p): p is StartEndRowCol => p !== undefined)
        .map((p) => `${p.rowStart}:${p.colStart}`)
    );

    const preview = new Set(copiedPositions.map((p) => `${p.rowStart + rowOffset}:${p.colStart + colOffset}`));

    const tableRows = tableScale?.maxRows ?? 0;
    const tableCols = tableScale?.maxColumns ?? 0;
    // Grid goes from row/col 1 up to the max of: current table size, paste preview extent
    const previewMaxRow = copiedPositions.length ? Math.max(...copiedPositions.map((p) => p.rowEnd + rowOffset)) : 0;
    const previewMaxCol = copiedPositions.length ? Math.max(...copiedPositions.map((p) => p.colEnd + colOffset)) : 0;
    const gridMaxRow = Math.min(MAX_GRID_SIZE, Math.max(tableRows, previewMaxRow, 1));
    const gridMaxCol = Math.min(MAX_GRID_SIZE, Math.max(tableCols, previewMaxCol, 1));

    // Build cell state array in row-major order, 1-indexed
    const cellStates: CellState[] = [];
    for (let r = 1; r <= gridMaxRow; r++) {
      for (let c = 1; c <= gridMaxCol; c++) {
        const key = `${r}:${c}`;
        const isOccupied = occupied.has(key);
        const isPreview = preview.has(key);
        const inBounds = r <= tableRows && c <= tableCols;
        if (isPreview && isOccupied) cellStates.push("conflict");
        else if (isPreview) cellStates.push(inBounds ? "preview" : "preview-expand");
        else if (isOccupied) cellStates.push("occupied");
        else if (!inBounds) cellStates.push("expand");
        else cellStates.push("empty");
      }
    }

    return { cellStates, gridMaxRow, gridMaxCol };
  }, [tableScale, positionInstructionsToPaste, copiedPositionInstructions, pasteAnchorRow, pasteAnchorCol]);

  const headerStyle: React.CSSProperties = {
    fontSize: 10,
    color: "#9e9e9e",
    textAlign: "center",
    lineHeight: `${CELL_PX}px`,
    userSelect: "none",
  };

  return (
    <div>
      <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 320 }}>
        <div
          style={{
            display: "grid",
            // 18px column for row-number labels, then one column per table column
            gridTemplateColumns: `18px repeat(${gridMaxCol}, ${CELL_PX}px)`,
            gap: 1,
            width: "fit-content",
          }}
        >
          {/* Top-left corner spacer */}
          <div />
          {/* Column headers (1-indexed) */}
          {Array.from({ length: gridMaxCol }, (_, i) => (
            <div key={`ch-${i}`} style={headerStyle}>
              {i + 1}
            </div>
          ))}

          {/* Rows (1-indexed) */}
          {Array.from({ length: gridMaxRow }, (_, ri) => {
            const r = ri + 1;
            return [
              // Row header
              <div key={`rh-${r}`} style={{ ...headerStyle, textAlign: "right", paddingRight: 2 }}>
                {r}
              </div>,
              // Cells
              ...Array.from({ length: gridMaxCol }, (_, ci) => {
                const c = ci + 1;
                const state = cellStates[ri * gridMaxCol + ci];
                return (
                  <div
                    key={`${r}:${c}`}
                    onClick={() => {
                      setPasteAnchorRow(r);
                      setPasteAnchorCol(c);
                    }}
                    title={`Row ${r}, Col ${c}`}
                    style={{
                      width: CELL_PX,
                      height: CELL_PX,
                      background: CELL_BACKGROUNDS[state],
                      border: "1px solid #e0e0e0",
                      cursor: "pointer",
                      boxSizing: "border-box",
                    }}
                  />
                );
              }),
            ];
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", marginTop: 8 }}>
        {LEGEND.map(({ state, label }) => (
          <div key={state} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div
              style={{
                width: 12,
                height: 12,
                background: CELL_BACKGROUNDS[state],
                border: "1px solid #e0e0e0",
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};
